import type { IDialogClient } from '@kit/dialog/core/dialogClient/types';
import type { IDialogObserver } from '@kit/dialog/core/dialogObserver/types';
import type { IDialogStore } from '@kit/dialog/core/dialogStore/types';
import { EDialogStatus, EDialogType } from '@kit/dialog/core/enums';
import type {
	IIdGenerator,
	TDialog,
	TDialogComponent,
	TDialogProps,
} from '@kit/dialog/core/types';

export class DialogClient implements IDialogClient {
	constructor(
		private readonly store: IDialogStore<
			TDialog<// eslint-disable-next-line @typescript-eslint/no-explicit-any
			any>
		>,
		private readonly observer: IDialogObserver,
		private readonly idGenerator: IIdGenerator,
	) {}

	public create<TProps, TClosePayload = void>(
		component: TDialogComponent<TProps, TClosePayload>,
		props?: TDialogProps<TProps, TClosePayload>,
	) {
		let id = this.idGenerator();

		let dialog: TDialog<TProps, TClosePayload> = {
			id,
			type: EDialogType.SHEET,
			status: EDialogStatus.INITIAL,

			component,
			props: props ?? null,

			close: () => this.close(id),
			remove: () => this.remove(id),
		};

		this.store.add(dialog);

		this.notify();

		return dialog;
	}

	public remove(id: string) {
		let result = this.store.remove(id);

		this.notify();

		return result;
	}

	public open(id: string) {
		let item = this.store.read(id);

		if (!item || item?.status === EDialogStatus.OPEN) {
			return;
		}

		this.store.update(item.id, {
			status: EDialogStatus.OPEN,
		});

		this.notify();

		return item;
	}

	public close(id: string) {
		let item = this.store.read(id);

		if (!item) {
			return;
		}

		this.store.update(item.id, {
			status: EDialogStatus.CLOSED,
		});

		this.notify();

		return item;
	}

	public getSnapshot() {
		return this.store.snapshot;
	}

	public dispose() {
		this.store.dispose();
		this.observer.dispose();
	}

	public subscribe(listener: () => void) {
		return this.observer.subscribe(listener);
	}

	private notify() {
		this.observer.notify();
	}
}
